import { Component, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { ToastItem, ToastTone } from '../../models/toast.models';
import { Icon } from '../icon/icon.component';

interface ToneConfig {
  icon: string;
  accent: string;
  iconClass: string;
}

const TONE_CONFIG: Record<ToastTone, ToneConfig> = {
  success: { icon: 'toast-success', accent: 'var(--color-pitch)', iconClass: 'text-pitch' },
  error:   { icon: 'toast-error',   accent: 'var(--color-rojo)',  iconClass: 'text-rojo'  },
  warn:    { icon: 'toast-warn',    accent: 'var(--color-amber)', iconClass: 'text-amber' },
  info:    { icon: 'toast-info',    accent: 'var(--color-sky)',   iconClass: 'text-sky'   },
};

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [NgClass, Icon],
  styles: [`
    :host { display: block; }

    .toast-item {
      animation: toast-in 220ms cubic-bezier(.2,.8,.2,1) both;
    }

    @keyframes toast-in {
      from { transform: translateX(20px) translateY(8px); opacity: 0; }
      to   { transform: translateX(0) translateY(0); opacity: 1; }
    }

    .progress-bar {
      transform-origin: left center;
      animation: toast-progress var(--toast-duration) linear forwards;
    }

    @keyframes toast-progress {
      from { transform: scaleX(1); }
      to   { transform: scaleX(0); }
    }
  `],
  template: `
    <div
      class="toast-item relative grid gap-x-3 gap-y-1 w-full
             bg-bg-1 border border-border rounded-xl
             px-4 py-3.5 shadow-[0_12px_36px_rgba(0,0,0,0.45)]
             overflow-hidden pointer-events-auto"
      style="grid-template-columns: auto 1fr auto"
    >
      @if (toast().duration > 0) {
        <div class="absolute left-0 right-0 bottom-0 h-0.5 bg-bg-3">
          <div
            class="progress-bar h-full opacity-55"
            [style.background]="toneConfig.accent"
            [style.--toast-duration]="toast().duration + 'ms'"
          ></div>
        </div>
      }

      <app-icon
        [name]="toneConfig.icon"
        [size]="18"
        [ngClass]="toneConfig.iconClass"
        class="self-center"
      />

      <p class="self-center text-[13px] font-semibold text-text-0 leading-snug">{{ toast().title }}</p>

      <button
        (click)="dismissed.emit(toast().id)"
        class="self-start p-0.5 bg-transparent border-none cursor-pointer text-text-3 hover:text-text-1 transition-colors"
      >
        <app-icon name="x" [size]="13" />
      </button>

      @if (toast().body) {
        <p class="col-start-2 text-[11.5px] text-text-2 leading-snug">{{ toast().body }}</p>
      }

      @if (toast().action) {
        <button
          (click)="onAction()"
          class="col-start-2 text-left text-[12px] font-bold underline underline-offset-2 bg-transparent border-none cursor-pointer"
          [style.color]="toneConfig.accent"
        >
          {{ toast().action!.label }}
        </button>
      }
    </div>
  `,
})
export class ToastComponent {
  readonly toast = input.required<ToastItem>();
  readonly dismissed = output<number>();

  get toneConfig(): ToneConfig {
    return TONE_CONFIG[this.toast().tone] ?? TONE_CONFIG.info;
  }

  onAction(): void {
    this.toast().action?.onClick();
    this.dismissed.emit(this.toast().id);
  }
}
